/**
 * Forme de `app.config.ts`, a la racine de l'app. C'est le seul endroit ou une app
 * se configure : le template ne lit jamais autre chose et l'app n'a pas a toucher
 * un fichier du template pour se decrire.
 */

export type RetentionPolicy = {
  /** Duree de conservation d'un compte inactif, en jours (RGPD). */
  readonly accountDays: number;
  /** Duree de conservation des evenements d'analyse, en jours. */
  readonly analyticsDays: number;
  /** Duree de conservation des journaux applicatifs, en jours. */
  readonly logDays: number;
  /** Duree de vie du cookie interne d'attribution (source de campagne), en jours. */
  readonly attributionDays: number;
  /** Adresse et telephone de livraison, en jours apres l'expedition (vente de biens). 90 par defaut. */
  readonly shippingDays?: number | undefined;
};

export type AppFeatures = {
  readonly auth: boolean;
  readonly billing: boolean;
  readonly llm: boolean;
};

/**
 * Identite de l'editeur. Le template ne peut pas la connaitre : elle est propre a
 * chaque app et engage une personne reelle. Les valeurs arrivent vides et se voient
 * (les pages legales affichent un avertissement, `/metrics` les liste, le backend
 * ecrit un avertissement au demarrage). A remplir avant la mise en ligne.
 */
export type PublisherIdentity = {
  /** Denomination sociale, ou nom et prenom pour un entrepreneur individuel. */
  readonly name: string;
  /** Forme juridique, et capital social s'il y en a un. */
  readonly legalForm: string;
  /** Adresse postale publiee (siege ou etablissement). */
  readonly address: string;
  /** Adresse de contact publiee. Vide = `contactEmail` de l'app. */
  readonly email: string;
  /** Telephone publie : exige par la LCEN pour l'editeur d'un service en ligne. */
  readonly phone: string;
  /** SIREN ou SIRET, et ville du greffe pour une societe. */
  readonly registration: string;
  /** Numero de TVA intracommunautaire. Vide si l'editeur n'y est pas assujetti. */
  readonly vatNumber: string;
  /**
   * Mention de TVA affichee dans les CGV et sur les factures. Deux cas usuels :
   * la franchise en base ("TVA non applicable, article 293 B du CGI") ou le taux
   * applique. Exigee des que l'option `billing` est active.
   */
  readonly vatNotice: string;
  /** Directeur de la publication (LCEN). */
  readonly publicationDirector: string;
};

/** Hebergeur du service : la LCEN impose de publier son nom, son adresse et son telephone. */
export type HostIdentity = {
  readonly name: string;
  readonly address: string;
  readonly phone: string;
};

/**
 * Mediateur de la consommation. Obligatoire pour un professionnel qui vend a des
 * consommateurs (code de la consommation, article L612-1), donc exige quand l'option
 * `billing` est active.
 */
export type MediatorIdentity = {
  readonly name: string;
  readonly address: string;
  readonly url: string;
};

/**
 * Un traitement propre a l'app, ajoute a la page de confidentialite. Les trois textes sont du
 * francais public, ecrit par l'app : la page les affiche tels quels (espaces de bordure
 * retires), une phrase complete chacun.
 */
export type AppProcessing = {
  /** Ce qui est collecte, en une phrase complete. */
  readonly collected: string;
  /** Pourquoi, et sur quelle base legale. */
  readonly purpose: string;
  /** Combien de temps c'est garde. */
  readonly retention: string;
};

/**
 * Le compte tel que l'app le vit, quand ce n'est pas celui du template (une adresse electronique
 * et un lien a usage unique envoye par courrier, un compte garde apres la derniere connexion, une
 * suppression qui efface le compte et ses donnees rattachees). Une app qui connecte ses visiteurs
 * autrement (un service tiers, une adresse technique, jamais de courrier), ou dont l'effacement
 * fait autre chose, ne peut pas laisser les pages dire le contraire, et le texte du template n'est
 * pas a elle : elle ecrit ces quatre textes, les quatre ensemble (aucun n'est facultatif dans le
 * bloc). Du francais public, une phrase complete chacun, que la page affiche tel quel (espaces de
 * bordure retires). Ils ne servent qu'avec l'option `auth` : sans elle, l'app n'a pas de compte.
 * Dans l'ordre ou les pages les affichent.
 */
export type AppAccount = {
  /**
   * Remplace la ligne "Compte" de la section "Donnees traitees" de la politique de
   * confidentialite : ce qui est garde sur le compte, et comment on s'y connecte.
   */
  readonly collected: string;
  /**
   * Remplace la ligne "Compte" de la section "Durees de conservation" de la politique de
   * confidentialite : combien de temps le compte est garde, et ce qui arrive ensuite. Elle prend la
   * place de la phrase du template, qui annonce `retention.accountDays` jours apres la derniere
   * connexion : l'app y ecrit sa propre duree, et la tient d'accord avec son code.
   */
  readonly retention: string;
  /**
   * Remplace le premier paragraphe de la section "Compte" des CGU : comment on se connecte, et
   * ce dont le visiteur reste responsable.
   */
  readonly terms: string;
  /**
   * Remplace, sur la page "Mes donnees", la phrase sous les boutons d'export et de suppression :
   * ce qu'efface la suppression, et si elle se rattrape. Une suppression est irreversible : une app
   * dont l'effacement a d'autres effets (des donnees gardees ailleurs, un compte chez un
   * fournisseur) le dit ici, la phrase est sous les yeux avant le premier clic.
   */
  readonly erasure: string;
};

export type LegalIdentity = {
  readonly publisher: PublisherIdentity;
  readonly host: HostIdentity;
  readonly mediator: MediatorIdentity;
  /** Date de derniere mise a jour des pages legales, au format AAAA-MM-JJ. */
  readonly updatedAt: string;
  /** Ce que l'app vend : du numerique (defaut) ou des biens expedies. Change les CGV. */
  readonly sales?: "digital" | "goods" | undefined;
  /** Identifiant unique de la filiere emballages (loi AGEC), affiche dans les CGV s'il est donne. */
  readonly idu?: string | undefined;
  /**
   * Traitements propres a l'app (un pseudo, une presence, un historique). Chacun ajoute une
   * ligne aux sections "Donnees traitees", "Finalites et bases legales" et "Durees de
   * conservation" de la politique de confidentialite, apres celles du template et dans l'ordre
   * donne. Absent ou vide = la page est celle du template, telle quelle. Un texte vide est
   * signale comme une identite incomplete (`missingLegalIdentity`).
   */
  readonly processing?: readonly AppProcessing[] | undefined;
  /**
   * Le compte de l'app, quand il n'est pas celui du template (une connexion par lien a usage
   * unique envoye par courrier electronique, un compte garde apres la derniere connexion, une
   * suppression qui efface le compte et ses donnees rattachees). Quatre textes, tous requis dans le
   * bloc : `collected` prend la place de la ligne "Compte" des donnees traitees de la politique
   * de confidentialite, `retention` celle de la ligne "Compte" de ses durees de conservation,
   * `terms` celle du premier paragraphe de la section "Compte" des CGU (le second, la
   * suppression du compte, reste), `erasure` celle de la phrase sous les boutons de la page "Mes
   * donnees". Absent = les textes du template, tels quels : sans ce bloc, rien ne change. Sans
   * l'option `auth`, il ne fait rien non plus. Un texte vide est signale comme une identite
   * incomplete (`missingLegalIdentity`).
   */
  readonly account?: AppAccount | undefined;
};

/**
 * Un produit vendu par l'app (option `billing`).
 *
 * `key` sert trois fois, et c'est voulu : c'est la cle du droit ouvert dans la base de
 * l'app, le `lookup_key` du prix cote Stripe, et le nom du produit dans les evenements
 * remontes au Lab. Une seule chaine a suivre du catalogue jusqu'au P&L.
 *
 * Le prix vit ici et non dans Stripe parce qu'un changement de prix est une decision
 * humaine (spec 6.4) : il se lit dans une revue de code, et il se propage a Stripe par
 * l'API, pas l'inverse. Attention : changer `amountCents` ne change pas un prix Stripe
 * deja cree ; il faut une nouvelle `key`, sinon les abonnements en cours et le catalogue
 * se contrediraient.
 */
export type BillingProduct = {
  readonly key: string;
  readonly label: string;
  readonly amountCents: number;
  /** Devise ISO 4217, trois lettres. */
  readonly currency: string;
  readonly mode: "payment" | "subscription";
};

export type BillingCatalogue = {
  /** Produit propose quand le checkout est demande sans en nommer un. */
  readonly defaultProduct: string;
  readonly products: readonly BillingProduct[];
};

/**
 * Mise en page commune. `width` regle la colonne de l'en-tete, du contenu et du pied de page :
 * "narrow" (768 px, le defaut) pour une app de texte et de formulaires, "wide" (1 152 px) pour
 * une app qui montre une grille. Les pages de la couche Lab gardent 768 px dans les deux cas.
 */
export type AppLayout = {
  readonly width?: "narrow" | "wide" | undefined;
};

export type AppConfig = {
  readonly slug: string;
  readonly name: string;
  readonly description: string;
  readonly domain: string;
  readonly contactEmail: string;
  readonly locale: string;
  readonly features: AppFeatures;
  readonly retention: RetentionPolicy;
  readonly legal: LegalIdentity;
  /** Mise en page commune. Absent = colonne etroite : une app existante ne change pas. */
  readonly layout?: AppLayout | undefined;
  /** Catalogue de l'app. Present seulement avec l'option `billing`. */
  readonly billing?: BillingCatalogue | undefined;
};

/** Le produit demande, ou celui par defaut. `null` quand la cle n'est pas au catalogue. */
export function findBillingProduct(
  config: AppConfig,
  key: string | undefined,
): BillingProduct | null {
  const catalogue = config.billing;
  if (!catalogue) return null;
  const wanted = key ?? catalogue.defaultProduct;
  return catalogue.products.find((product) => product.key === wanted) ?? null;
}

/** Identite typee : sert a faire verifier `app.config.ts` par TypeScript. */
export function defineAppConfig(config: AppConfig): AppConfig {
  return config;
}

/** Champs d'identite exiges quelle que soit l'app. */
const ALWAYS_REQUIRED = [
  "legal.publisher.name",
  "legal.publisher.legalForm",
  "legal.publisher.address",
  "legal.publisher.phone",
  "legal.publisher.registration",
  "legal.publisher.publicationDirector",
  "legal.host.name",
  "legal.host.address",
  "legal.host.phone",
  "legal.updatedAt",
] as const;

/** Champs exiges en plus des que l'app vend quelque chose (option `billing`). */
const BILLING_REQUIRED = [
  "legal.publisher.vatNotice",
  "legal.mediator.name",
  "legal.mediator.url",
] as const;

/**
 * Champs exiges en plus des que l'app vend des biens expedies (`legal.sales = "goods"`) :
 * l'identifiant unique de la filiere emballages figure dans les CGV et les mentions legales.
 */
const GOODS_REQUIRED = ["legal.idu"] as const;

/** Les trois textes d'un traitement propre a l'app, dans l'ordre ou la page les affiche. */
const PROCESSING_REQUIRED: readonly (keyof AppProcessing)[] = ["collected", "purpose", "retention"];

/**
 * Les quatre textes du compte de l'app, dans l'ordre des pages : confidentialite (donnees puis
 * durees), CGU, « Mes donnees ».
 */
const ACCOUNT_REQUIRED: readonly (keyof AppAccount)[] = [
  "collected",
  "retention",
  "terms",
  "erasure",
];

/**
 * Chemin d'un texte de traitement, tel que `missingLegalIdentity` le liste et que la page le
 * nomme a la place d'un texte vide : `legal.processing[1].retention` est la duree de
 * conservation du deuxieme traitement (le premier est le rang 0).
 */
export function processingPath(index: number, field: keyof AppProcessing): string {
  return `legal.processing[${index}].${field}`;
}

/**
 * Chemin d'un texte du compte de l'app, construit comme celui d'un traitement : c'est lui que
 * `missingLegalIdentity` liste et que la page nomme a la place d'un texte vide
 * (`legal.account.terms`).
 */
export function accountPath(field: keyof AppAccount): string {
  return `legal.account.${field}`;
}

function valueAt(config: AppConfig, path: string): string {
  let current: unknown = config;
  // Les points et les crochets separent les etapes : `legal.processing[0].collected`.
  for (const key of path.split(/[.[\]]+/).filter(Boolean)) {
    if (typeof current !== "object" || current === null) return "";
    current = (current as Record<string, unknown>)[key];
  }
  return typeof current === "string" ? current.trim() : "";
}

/**
 * Liste des champs d'identite encore vides. Vide = l'app peut etre publiee ;
 * non vide = les pages legales sont incompletes et le disent a l'ecran. Les quatre textes du
 * compte de l'app (`legal.account`, seulement avec l'option `auth`, comme les pages) puis les
 * trois textes de chaque traitement propre a l'app (`legal.processing`) en font partie, a la
 * suite des champs de l'identite.
 */
export function missingLegalIdentity(config: AppConfig): string[] {
  const required: string[] = [...ALWAYS_REQUIRED];
  if (config.features.billing) required.push(...BILLING_REQUIRED);
  if (isGoods(config)) required.push(...GOODS_REQUIRED);
  if (config.features.auth && config.legal.account) {
    for (const field of ACCOUNT_REQUIRED) required.push(accountPath(field));
  }
  const processingCount = config.legal.processing?.length ?? 0;
  for (let index = 0; index < processingCount; index += 1) {
    for (const field of PROCESSING_REQUIRED) required.push(processingPath(index, field));
  }
  return required.filter((path) => valueAt(config, path) === "");
}

/** Adresse de contact publiee : celle de l'editeur si elle est renseignee, sinon celle de l'app. */
export function publishedContactEmail(config: AppConfig): string {
  return config.legal.publisher.email.trim() || config.contactEmail;
}

/** Vrai quand l'app vend (option `billing`) des biens expedies : les CGV parlent alors de livraison. */
export function isGoods(config: AppConfig): boolean {
  return config.features.billing && config.legal.sales === "goods";
}
